/**
 * Every chart type, in one place.
 *
 * Modelled on `src/components/charts/kpi/kpiLayouts.js`, which already proves
 * the pattern: one list, one index, one resolver with a safe default. Adding a
 * chart type is one entry here — not a new branch in each of the ~36 places
 * that currently compare a type string.
 *
 * - `family`        groups types that render through the same view.
 * - `engine`        the ECharts defaults for the type, replacing the old
 *                   `seriesSettingsFor()` if/else chain.
 * - `capabilities`  what the builder should offer. Replaces conditions like
 *                   `['kpicard','table'].every(t => t !== chartType)`.
 * - `requires`      what the bound data must provide for the type to be
 *                   selectable, so the gallery can explain a disabled option
 *                   instead of rendering something meaningless.
 * - `settings`      the default per-type settings block (see chartSettings.js).
 */

// Axis keys are fixed and shared, so a per-type override still carries the
// label options the settings schema binds. Spelling them out here is what lets
// the schema-path spec catch a typo instead of it silently writing a new key.
const AXIS = (overrides = {}) => ({
    show: true,
    type: 'value',
    position: 'left',
    alignTicks: false,
    axisLabel: { format: 'ShortNumber', separator: '-', customFormat: '', rotate: 0 },
    ...overrides,
})

const CARTESIAN_SETTINGS = () => ({
    xAxis: AXIS({ type: 'category', position: 'bottom', axisLabel: { format: 'LongDate', separator: '-', customFormat: '', rotate: 60 } }),
    yAxis: AXIS(),
    yAxisSecondary: AXIS({ show: false, position: 'right', alignTicks: true }),
    legend: { show: true, labelType: 'shortTitle', icon: 'rect', orient: 'horizontal', left: 'center', top: 'bottom', padding: 5 },
    tooltip: {
        show: true,
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        backgroundColor: '#ffffff',
        borderColor: '#ccc',
        borderWidth: 1,
        padding: 10,
    },
})

const PIE_SETTINGS = () => ({
    radius: null,
    roseType: null,
    labelMode: 'percent',
    legend: { show: true, labelType: 'shortTitle', icon: 'rect', orient: 'vertical', left: 'right', top: 'middle', padding: 5 },
    tooltip: { show: true, trigger: 'item', axisPointer: { type: 'none' }, backgroundColor: '#ffffff', borderColor: '#ccc', borderWidth: 1, padding: 10 },
})

const CARTESIAN_CAPS = {
    series: true, axes: true, secondaryAxis: true, stacking: true,
    legend: true, tooltip: true, dataLabels: true, palette: true,
    overlays: true, multiSource: true, pivot: true,
    // Only rendered by the table and the KPI card; an ECharts series has
    // nothing that reads a cell format.
    conditionalFormatting: false,
}

const CIRCULAR_CAPS = {
    series: true, axes: false, secondaryAxis: false, stacking: false,
    legend: true, tooltip: true, dataLabels: true, palette: true,
    overlays: false, multiSource: false, pivot: true,
    conditionalFormatting: false,
}

export const CHART_TYPES = [
    {
        value: 'column', label: 'Column', family: 'cartesian',
        icon: 'stacked_bar_chart', iconClass: '',
        engine: { seriesType: 'bar', categoryAxis: 'x', valueAxis: 'y', base: { symbol: 'none' } },
        capabilities: CARTESIAN_CAPS,
        requires: { measures: 1, dimension: true },
        settings: CARTESIAN_SETTINGS,
        gridDefaults: { w: 12, h: 10 },
    },
    {
        value: 'bar', label: 'Bar', family: 'cartesian',
        icon: 'bar_chart', iconClass: 'rotate-90',
        // The only type whose axes are flipped — the single most likely source
        // of an overlay landing on the wrong axis.
        engine: { seriesType: 'bar', categoryAxis: 'y', valueAxis: 'x', base: { symbol: 'none' } },
        capabilities: CARTESIAN_CAPS,
        requires: { measures: 1, dimension: true },
        settings: () => ({ ...CARTESIAN_SETTINGS(), xAxis: AXIS({ position: 'bottom' }), yAxis: AXIS({ type: 'category' }) }),
        gridDefaults: { w: 12, h: 10 },
    },
    {
        value: 'line', label: 'Line', family: 'cartesian',
        icon: 'show_chart', iconClass: '',
        engine: { seriesType: 'line', categoryAxis: 'x', valueAxis: 'y', base: { smooth: true, symbol: 'none' } },
        capabilities: CARTESIAN_CAPS,
        requires: { measures: 1, dimension: true },
        settings: CARTESIAN_SETTINGS,
        gridDefaults: { w: 12, h: 10 },
    },
    {
        value: 'area', label: 'Area', family: 'cartesian',
        icon: 'area_chart', iconClass: '',
        engine: { seriesType: 'line', categoryAxis: 'x', valueAxis: 'y', base: { smooth: true, symbol: 'none', areaStyle: {} } },
        capabilities: CARTESIAN_CAPS,
        requires: { measures: 1, dimension: true },
        settings: CARTESIAN_SETTINGS,
        gridDefaults: { w: 12, h: 10 },
    },
    {
        value: 'scatter', label: 'Scatter', family: 'cartesian',
        icon: 'scatter_plot', iconClass: '',
        engine: { seriesType: 'scatter', categoryAxis: 'x', valueAxis: 'y', base: { symbolSize: 20 } },
        capabilities: { ...CARTESIAN_CAPS, stacking: false },
        requires: { measures: 1, dimension: true },
        settings: () => ({ ...CARTESIAN_SETTINGS(), xAxis: AXIS({ position: 'bottom' }) }),
        gridDefaults: { w: 12, h: 10 },
    },
    {
        value: 'pie', label: 'Pie', family: 'circular',
        icon: 'pie_chart', iconClass: '',
        engine: { seriesType: 'pie', categoryAxis: null, valueAxis: null, base: { symbol: 'none' } },
        capabilities: CIRCULAR_CAPS,
        // A categorical dimension, not just any: slices of a date axis are
        // slices of the calendar rather than of the thing being measured.
        requires: { measures: 1, categoricalDimension: true },
        settings: PIE_SETTINGS,
        gridDefaults: { w: 6, h: 10 },
    },
    {
        value: 'donut', label: 'Donut', family: 'circular',
        icon: 'donut_large', iconClass: '',
        engine: { seriesType: 'pie', categoryAxis: null, valueAxis: null, base: { symbol: 'none', radius: ['30%', '50%'] } },
        capabilities: CIRCULAR_CAPS,
        requires: { measures: 1, categoricalDimension: true },
        settings: () => ({ ...PIE_SETTINGS(), radius: ['30%', '50%'] }),
        gridDefaults: { w: 6, h: 10 },
    },
    {
        value: 'table', label: 'Table', family: 'table',
        icon: 'table_chart', iconClass: '',
        engine: null,
        capabilities: {
            series: false, axes: false, secondaryAxis: false, stacking: false,
            legend: false, tooltip: false, dataLabels: false, palette: false,
            overlays: false, multiSource: false, pivot: true,
            conditionalFormatting: true,
        },
        requires: {},
        settings: () => ({
            styling: {
                size: 'normal', fontSize: 'md', showGridLines: false, stripedRows: true,
                rowHover: true, resizableColumns: false, columnResizeMode: 'fit', reorderableColumns: false,
            },
            interactivity: {
                selectable: false, sortable: true, sortMode: 'multiple', removableSort: true,
                filterable: true, exportable: true,
                emptyMessage: 'No data found.',
                pagination: {
                    paginator: true, rows: 10, position: 'bottom',
                    rowsPerPageOptions: [10, 20, 30, 40, 50, 100],
                },
            },
            // A footer row across every numeric column. Per-column overrides
            // are a separate, later concern — this is the table-wide default.
            totals: { show: false, aggregation: 'sum' },
        }),
        gridDefaults: { w: 12, h: 10 },
    },
    {
        value: 'kpicard', label: 'KPI Card', family: 'kpi',
        icon: 'pin', iconClass: '',
        engine: null,
        capabilities: {
            series: false, axes: false, secondaryAxis: false, stacking: false,
            legend: false, tooltip: false, dataLabels: false, palette: false,
            overlays: false, multiSource: false, pivot: false,
            conditionalFormatting: true,
        },
        requires: { measures: 1 },
        // No `numberFormat` here on purpose: the card reads the resolved
        // settings, and a block-level copy would shadow `common.numberFormat`
        // — so the universal Numbers group would silently do nothing.
        settings: () => ({
            layout: 'standard',
            valueFontSize: 'xl',
            // `card` is what reaches KPICard as `kpiCardOptions`; the key names
            // are its, not ours, so the mapping stays a rename-free pass-through.
            card: {
                valuePrefix: null,
                valueSuffix: null,
                comparisonText: null,
                footerText: null,
                displayCardValueType: 'value',
                // How the delta badge reads its change. Percent is what every
                // card showed before this was a choice.
                changeDisplay: 'percentChange',
                displayComparison: true,
                displayComparisonValue: true,
                displayComparisonDelta: true,
            },
        }),
        gridDefaults: { w: 6, h: 5 },
    },
]

export const DEFAULT_CHART_TYPE = 'column'

const BY_VALUE = new Map(CHART_TYPES.map(type => [type.value, type]))

/** Never returns undefined — an unknown type falls back to the default. */
export function resolveChartType(value) {
    return BY_VALUE.get(value) ?? BY_VALUE.get(DEFAULT_CHART_TYPE)
}

export function chartTypeExists(value) {
    return BY_VALUE.has(value)
}

/** `can('column', 'axes')` — the replacement for the type-string comparisons. */
export function can(value, capability) {
    return Boolean(resolveChartType(value).capabilities[capability])
}

export function chartTypesWith(capability) {
    return CHART_TYPES.filter(type => type.capabilities[capability]).map(type => type.value)
}

/** Default settings block for a type, fresh each call so nothing is shared. */
export function defaultSettingsFor(value) {
    return resolveChartType(value).settings()
}

/**
 * Whether the bound data can feed this type, and if not, why — so the gallery
 * can explain a disabled option rather than silently rendering nothing.
 *
 * @param {string} value
 * @param {{measures?: number, hasDimension?: boolean, hasCategoricalDimension?: boolean, hasTime?: boolean}} shape
 */
export function checkRequirements(value, shape = {}) {
    const { requires } = resolveChartType(value)
    const measures = shape.measures ?? 0

    if (requires.measures && measures < requires.measures) {
        return { ok: false, reason: `needs at least ${requires.measures} measure${requires.measures === 1 ? '' : 's'}` }
    }

    if (requires.dimension && !shape.hasDimension) {
        return { ok: false, reason: 'needs a dimension — the bound queries return a single total' }
    }

    if (requires.categoricalDimension && !shape.hasCategoricalDimension) {
        return {
            ok: false,
            reason: shape.hasTime
                ? 'needs something to slice by — add a breakdown, or it would just slice the calendar'
                : 'needs a breakdown to slice by',
        }
    }

    if (requires.time && !shape.hasTime) {
        return { ok: false, reason: 'needs a time dimension' }
    }

    return { ok: true, reason: null }
}

/** The picker list, shaped as the existing chart-type store exposes it. */
export const CHART_TYPE_OPTIONS = CHART_TYPES.map(({ value, label, icon, iconClass }) => ({
    value, title: label, icon, class: iconClass,
}))

export default CHART_TYPES
